import { useState } from 'react';
import { ArrowUp, ArrowDown, ArrowLeftRight } from 'lucide-react';

/**
 * PitchLineup — premium TV-style formation diagram.
 *
 * Renders each starting XI on a green pitch using API-Football's `grid`
 * field, formatted "row:col" (e.g. "1:1" = GK, "4:3" = striker row).
 * Lower row = closer to own goal.
 *
 * Each team occupies its OWN half: the away team is flipped to the top
 * (attacking down) and the home team sits on the bottom (attacking up),
 * mirrored like every TV graphic / FotMob. When only one lineup is
 * available it uses the full pitch.
 */
const PLAYER_DOT_TONES = {
  home: { ring: 'ring-sky-300', bg: 'bg-sky-600' },
  away: { ring: 'ring-rose-300', bg: 'bg-rose-600' },
};

const initialsOf = (fullName) => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const parseGrid = (g) => {
  if (!g || typeof g !== 'string' || !g.includes(':')) return null;
  const [row, col] = g.split(':').map((n) => parseInt(n, 10));
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  return { row, col };
};

const PLAYER_PHOTO_BASE = 'https://media.api-sports.io/football/players';

const PlayerDot = ({ player, tone }) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  const showPhoto = player.id && !photoFailed;
  return (
    <div
      className={`relative w-11 h-11 sm:w-12 sm:h-12 rounded-full ${tone.bg} text-white ring-[2.5px] ${tone.ring} flex items-center justify-center text-[11px] font-extrabold overflow-hidden`}
      style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.25)' }}
      title={`${player.number ? '#' + player.number + ' ' : ''}${player.name}${player.pos ? ' · ' + player.pos : ''}`}
    >
      {showPhoto && (
        <img
          src={`${PLAYER_PHOTO_BASE}/${player.id}.png`}
          alt={player.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setPhotoFailed(true)}
        />
      )}
      {!showPhoto && (player.number != null ? player.number : initialsOf(player.name))}
      {player.number != null && (
        <span className="absolute -bottom-1 -right-1 min-w-[16px] h-[16px] px-0.5 rounded-full bg-black/90 text-white text-[8px] font-extrabold flex items-center justify-center ring-1 ring-white/40">
          {player.number}
        </span>
      )}
      {player.subbed_on && (
        <span
          className="absolute -top-1 -right-1 w-[15px] h-[15px] rounded-full bg-emerald-500 text-white flex items-center justify-center ring-1 ring-white/50"
          title={`On at ${player.sub_minute}'${player.replaced ? ' for ' + player.replaced : ''}`}
        >
          <ArrowUp className="w-2.5 h-2.5" strokeWidth={3} />
        </span>
      )}
    </div>
  );
};

const TeamHalf = ({ team, side, tone, flip }) => {
  if (!team) return null;
  const startXI = team.start_xi || team.startXI || [];
  // Group players by row, then sort each row left→right by their grid column.
  const rows = {};
  let maxRow = 0;
  startXI.forEach((p) => {
    const g = parseGrid(p.grid);
    const row = g?.row ?? 0;
    (rows[row] = rows[row] || []).push(p);
    if (row > maxRow) maxRow = row;
  });
  Object.values(rows).forEach((arr) =>
    arr.sort((a, b) => (parseGrid(a.grid)?.col ?? 0) - (parseGrid(b.grid)?.col ?? 0)),
  );
  const totalRows = Math.max(maxRow, 1);

  return (
    <div className="relative w-full h-full" data-testid={`pitch-half-${side}`}>
      {startXI.map((p, idx) => {
        const g = parseGrid(p.grid);
        if (!g) return null;
        const rowFromBottom = flip ? totalRows - g.row + 1 : g.row;
        // Keep dots inside the half with a small margin so labels don't clip.
        const topPct = ((totalRows - rowFromBottom) / totalRows) * 84 + 8;
        const rowPlayers = rows[g.row] || [];
        const posInRow = rowPlayers.indexOf(p);
        const leftPct = ((posInRow + 0.5) / Math.max(rowPlayers.length, 1)) * 100;
        return (
          <div
            key={p.id || `${side}-${idx}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ top: `${topPct}%`, left: `${leftPct}%` }}
            data-testid={`pitch-player-${p.id || idx}`}
          >
            <PlayerDot player={p} tone={tone} />
            <div className="mt-1 px-1.5 py-0.5 rounded-md bg-black/55 backdrop-blur-sm max-w-[84px] truncate text-center text-[9px] font-bold text-white leading-none">
              {p.name?.split(' ').slice(-1)[0] || p.name}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Crisp SVG pitch markings (viewBox matches the 68×105 pitch ratio so circles
// stay perfectly round). Penalty arcs are drawn as clipped circles to avoid
// fragile arc-sweep maths.
const PitchMarkings = () => {
  const stroke = 'rgba(255,255,255,0.5)';
  const sw = 0.4;
  return (
    <svg
      viewBox="0 0 68 105"
      className="absolute inset-0 w-full h-full pointer-events-none"
      fill="none"
      stroke={stroke}
      strokeWidth={sw}
    >
      <defs>
        <clipPath id="clipTopArc"><rect x="0" y="16.5" width="68" height="88.5" /></clipPath>
        <clipPath id="clipBotArc"><rect x="0" y="0" width="68" height="88.5" /></clipPath>
        <clipPath id="clipPitch"><rect x="0.5" y="0.5" width="67" height="104" rx="1.2" /></clipPath>
      </defs>

      {/* Boundary */}
      <rect x="0.5" y="0.5" width="67" height="104" rx="1.2" />
      {/* Halfway line + centre circle + spot */}
      <line x1="0.5" y1="52.5" x2="67.5" y2="52.5" />
      <circle cx="34" cy="52.5" r="9.15" />
      <circle cx="34" cy="52.5" r="0.5" fill="rgba(255,255,255,0.7)" stroke="none" />

      {/* Top box set */}
      <rect x="13.84" y="0.5" width="40.32" height="16" />
      <rect x="24.84" y="0.5" width="18.32" height="5" />
      <circle cx="34" cy="11" r="0.5" fill="rgba(255,255,255,0.7)" stroke="none" />
      <circle cx="34" cy="11" r="9.15" clipPath="url(#clipTopArc)" />

      {/* Bottom box set */}
      <rect x="13.84" y="88.5" width="40.32" height="16" />
      <rect x="24.84" y="99.5" width="18.32" height="5" />
      <circle cx="34" cy="94" r="0.5" fill="rgba(255,255,255,0.7)" stroke="none" />
      <circle cx="34" cy="94" r="9.15" clipPath="url(#clipBotArc)" />

      {/* Corner arcs */}
      <g clipPath="url(#clipPitch)">
        <circle cx="0.5" cy="0.5" r="1.2" />
        <circle cx="67.5" cy="0.5" r="1.2" />
        <circle cx="0.5" cy="104.5" r="1.2" />
        <circle cx="67.5" cy="104.5" r="1.2" />
      </g>
    </svg>
  );
};

const PitchLineup = ({ home, away }) => {
  const [showSubs, setShowSubs] = useState(false);
  if (!home && !away) return null;
  const homeXI = home?.start_xi || home?.startXI || [];
  const awayXI = away?.start_xi || away?.startXI || [];
  const bothTeams = homeXI.length > 0 && awayXI.length > 0;
  const soloTeam = bothTeams ? null : (homeXI.length ? home : away);
  const soloSide = home && homeXI.length ? 'home' : 'away';

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 shadow-xl shadow-black/30" data-testid="pitch-lineup">
      {/* Team header row with crests + formation */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-sky-950/60 via-black/50 to-rose-950/60">
        <div className="flex items-center gap-2 min-w-0">
          {home?.team_logo && <img src={home.team_logo} alt="" className="w-5 h-5 object-contain" />}
          <div className="text-xs font-extrabold text-white truncate">{home?.team_name}</div>
          {home?.formation && (
            <span className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 font-bold">{home.formation}</span>
          )}
        </div>
        <div className="flex items-center gap-2 min-w-0 justify-end">
          {away?.formation && (
            <span className="mr-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-bold">{away.formation}</span>
          )}
          <div className="text-xs font-extrabold text-white truncate">{away?.team_name}</div>
          {away?.team_logo && <img src={away.team_logo} alt="" className="w-5 h-5 object-contain" />}
        </div>
      </div>

      {/* The pitch */}
      <div
        className="relative w-full overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at 50% 45%, #1c904c 0%, #157039 55%, #0b4d27 100%)',
          aspectRatio: '68 / 105',
        }}
      >
        {/* Mowing stripes (horizontal bands) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'repeating-linear-gradient(180deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 9.09%, rgba(0,0,0,0.04) 9.09%, rgba(0,0,0,0.04) 18.18%)',
          }}
        />
        {/* Edge vignette for depth */}
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 90px rgba(0,0,0,0.55)' }} />

        <PitchMarkings />

        {bothTeams ? (
          <>
            <div className="absolute top-0 inset-x-0 h-1/2 overflow-visible">
              <TeamHalf team={away} side="away" tone={PLAYER_DOT_TONES.away} flip />
            </div>
            <div className="absolute bottom-0 inset-x-0 h-1/2 overflow-visible">
              <TeamHalf team={home} side="home" tone={PLAYER_DOT_TONES.home} flip={false} />
            </div>
          </>
        ) : (
          <div className="absolute inset-0 overflow-visible">
            <TeamHalf
              team={soloTeam}
              side={soloSide}
              tone={soloSide === 'home' ? PLAYER_DOT_TONES.home : PLAYER_DOT_TONES.away}
              flip={false}
            />
          </div>
        )}
      </div>

      {/* Coaches + subs collapsible */}
      <div className="bg-black/50 px-4 py-2.5 space-y-2">
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          {home?.coach && (
            <div data-testid="pitch-coach-home">
              <span className="text-zinc-500">Coach · </span>
              <span className="text-zinc-200">{home.coach}</span>
            </div>
          )}
          {away?.coach && (
            <div className="text-right" data-testid="pitch-coach-away">
              <span className="text-zinc-500">Coach · </span>
              <span className="text-zinc-200">{away.coach}</span>
            </div>
          )}
        </div>
        {/* In-match substitutions — who came on for whom, with minute */}
        {([home, away].some((t) => (t?.substitutions || []).length > 0)) && (
          <div className="grid grid-cols-2 gap-3 pt-1 pb-1 border-t border-white/10" data-testid="pitch-substitutions">
            {[home, away].map((t, i) => (
              <div key={i} className={i === 1 ? 'text-right' : ''}>
                <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500 mb-1.5 mt-2"
                     style={{ justifyContent: i === 1 ? 'flex-end' : 'flex-start' }}>
                  <ArrowLeftRight className="w-2.5 h-2.5" /> Subs
                </div>
                <div className="space-y-1">
                  {(t?.substitutions || []).length === 0 ? (
                    <div className="text-[10px] text-zinc-600">None</div>
                  ) : (
                    t.substitutions.map((s, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-1 text-[10px]"
                        style={{ justifyContent: i === 1 ? 'flex-end' : 'flex-start' }}
                        data-testid={`pitch-sub-${i}-${idx}`}
                      >
                        {i === 1 && <span className="text-zinc-600 tabular-nums">{s.minute}'</span>}
                        <span className="flex items-center gap-0.5 min-w-0">
                          <ArrowUp className="w-2.5 h-2.5 text-emerald-400 shrink-0" strokeWidth={3} />
                          <span className="text-emerald-300 truncate">{s.on}</span>
                        </span>
                        <span className="flex items-center gap-0.5 min-w-0">
                          <ArrowDown className="w-2.5 h-2.5 text-rose-400 shrink-0" strokeWidth={3} />
                          <span className="text-zinc-400 truncate">{s.off}</span>
                        </span>
                        {i === 0 && <span className="text-zinc-600 tabular-nums">{s.minute}'</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowSubs((s) => !s)}
          className="w-full text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white py-1.5 rounded bg-white/5 hover:bg-white/10 transition-colors"
          data-testid="pitch-toggle-subs"
        >
          {showSubs ? 'Hide bench' : 'Show bench'}
        </button>
        {showSubs && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            {[home, away].map((t, i) => (
              <div key={i}>
                <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{t?.team_name}</div>
                <div className="space-y-0.5">
                  {(t?.substitutes || []).slice(0, 10).map((p, idx) => (
                    <div key={p.id || idx} className="flex items-baseline gap-1.5 text-[10px] text-zinc-300">
                      <span className="text-zinc-600 tabular-nums w-5 shrink-0 text-right">{p.number ?? '-'}</span>
                      <span className="truncate">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PitchLineup;
